// Define MBTI types for clarity
type MbtiIE = 'I' | 'E'
type MbtiNS = 'N' | 'S'
type MbtiTF = 'T' | 'F'
type MbtiPJ = 'P' | 'J'

interface MbtiState {
  ie?: MbtiIE
  ns?: MbtiNS
  tf?: MbtiTF
  pj?: MbtiPJ
}

interface MbtiStepProps {
  value: string
  onChange: (mbti: string) => void
}

// Helper component for MBTI buttons
interface MbtiButtonProps {
  value: string
  currentValue?: string
  onClick: () => void
  children: React.ReactNode
}

const MbtiButton = ({ value, currentValue, onClick, children }: MbtiButtonProps) => (
  <button
    type='button'
    onClick={onClick}
    className={`w-[60px] h-[60px] flex items-center justify-center text-lg font-medium rounded-[24px] border-2 transition-all duration-300 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-[#FFA236] ${
      currentValue === value
        ? 'bg-[#FFA236] text-white border-[#FFA236]'
        : 'border-[#FFA236]/20 text-gray-700 bg-white/70 backdrop-blur-sm hover:border-[#FFA236]'
    }`}
  >
    {children}
  </button>
)

export function MbtiStep({ value, onChange }: MbtiStepProps) {
  // Parse the current MBTI string into state object
  const parseMbtiString = (mbtiString: string): MbtiState => {
    const state: MbtiState = {}

    if (mbtiString.includes('I')) state.ie = 'I'
    else if (mbtiString.includes('E')) state.ie = 'E'

    if (mbtiString.includes('N')) state.ns = 'N'
    else if (mbtiString.includes('S')) state.ns = 'S'

    if (mbtiString.includes('T')) state.tf = 'T'
    else if (mbtiString.includes('F')) state.tf = 'F'

    if (mbtiString.includes('J')) state.pj = 'J'
    else if (mbtiString.includes('P')) state.pj = 'P'

    return state
  }

  // Convert MBTI state object back to string
  const formatMbtiString = (mbtiState: MbtiState): string => {
    const parts = []
    if (mbtiState.ie) parts.push(mbtiState.ie)
    if (mbtiState.ns) parts.push(mbtiState.ns)
    if (mbtiState.tf) parts.push(mbtiState.tf)
    if (mbtiState.pj) parts.push(mbtiState.pj)
    return parts.join('')
  }

  const currentMbti = parseMbtiString(value)

  const handleMbtiChange = (dimension: keyof MbtiState, selectedValue: MbtiIE | MbtiNS | MbtiTF | MbtiPJ) => {
    const newMbti = { ...currentMbti }

    // If the same button is clicked again, remove it (reset that dimension)
    if (newMbti[dimension] === selectedValue) {
      delete newMbti[dimension]
    } else {
      newMbti[dimension] = selectedValue as any
    }

    onChange(formatMbtiString(newMbti))
  }

  return (
    <div className='h-full flex flex-col justify-center'>
      {' '}
      {/* Adjusted for better centering if content is short */}
      <div className='flex flex-col gap-4'>
        <div className='space-y-2 mb-6 text-center'>
          <h2 className='text-[24px] font-[700] text-[#020202]'>그럼 MBTI도 알려줄래?</h2>
          <p className='text-[14px] text-[#7D7D7D]'>(선택사항)</p>
        </div>

        {/* I/E Row */}
        <div className='grid grid-cols-2 gap-4 mx-auto w-max'>
          <MbtiButton value='I' currentValue={currentMbti.ie} onClick={() => handleMbtiChange('ie', 'I')}>
            I
          </MbtiButton>
          <MbtiButton value='E' currentValue={currentMbti.ie} onClick={() => handleMbtiChange('ie', 'E')}>
            E
          </MbtiButton>
        </div>

        {/* N/S Row */}
        <div className='grid grid-cols-2 gap-4 mx-auto w-max'>
          <MbtiButton value='N' currentValue={currentMbti.ns} onClick={() => handleMbtiChange('ns', 'N')}>
            N
          </MbtiButton>
          <MbtiButton value='S' currentValue={currentMbti.ns} onClick={() => handleMbtiChange('ns', 'S')}>
            S
          </MbtiButton>
        </div>

        {/* T/F Row */}
        <div className='grid grid-cols-2 gap-4 mx-auto w-max'>
          <MbtiButton value='T' currentValue={currentMbti.tf} onClick={() => handleMbtiChange('tf', 'T')}>
            T
          </MbtiButton>
          <MbtiButton value='F' currentValue={currentMbti.tf} onClick={() => handleMbtiChange('tf', 'F')}>
            F
          </MbtiButton>
        </div>

        {/* J/P Row */}
        <div className='grid grid-cols-2 gap-4 mx-auto w-max'>
          <MbtiButton value='J' currentValue={currentMbti.pj} onClick={() => handleMbtiChange('pj', 'J')}>
            J
          </MbtiButton>
          <MbtiButton value='P' currentValue={currentMbti.pj} onClick={() => handleMbtiChange('pj', 'P')}>
            P
          </MbtiButton>
        </div>
      </div>
    </div>
  )
}
